import { useRef, useState } from "react";

import { Button, Divider, Form, Input } from "antd";

import { useClickOutside, RVFormItem } from "@regal-voice/ui";
import { Row } from "Components/shared/Flexbox";

import sharedStyles from "../../SharedInputStyling.module.scss";
import { isValidUrl } from "../Utils";

export const linkValidator = {
    validator(_: any, value: any) {
        if (!value || value.length === 0) {
            return Promise.resolve();
        }

        if (!isValidUrl(value)) {
            return Promise.reject(new Error("Link is invalid."));
        }

        return Promise.resolve();
    },
};

export type LinkEditProps = {
    link: string;
    displayText: string;
    isNew: boolean;
    onClickOutside: () => void;
    onConfirm: (values: LinkDetails) => void;
    onRemove?: () => void;
};

export type LinkDetails = {
    displayText: string;
    title?: string; // leaving here for future support
    link: string;
};

export function LinkEdit({
    link,
    displayText,
    isNew,
    onClickOutside,
    onConfirm,
    onRemove,
}: LinkEditProps): JSX.Element {
    const [form] = Form.useForm();
    const formRef = useRef(null);
    const [formValid, setFormValid] = useState<boolean>(false);
    useClickOutside([formRef], onClickOutside);

    const initialValues = { displayText, link };

    const handleOnFinish = () => {
        const { displayText, link } = form.getFieldsValue();
        const finalUrl = /^http(s)?/.test(link) ? link : `http://${link}`;
        onConfirm({ displayText, link: finalUrl });
    };

    const handleChanges = () => {
        setFormValid(!form.getFieldsError().some((item) => item.errors.length > 0));
    };

    return (
        <div ref={formRef}>
            <Form
                form={form}
                className={sharedStyles.linkEditContainer}
                initialValues={initialValues}
                layout="vertical"
                onFinish={handleOnFinish}
                onFieldsChange={handleChanges}
            >
                <RVFormItem
                    name="displayText"
                    label="Display text"
                    rules={[{ required: true, message: "Display Text is required" }]}
                >
                    <Input placeholder="Text to be displayed" data-testid="edit-link-form-display-text" />
                </RVFormItem>
                <RVFormItem
                    name="link"
                    label="Link"
                    rules={[{ required: true, message: "Link is required" }, linkValidator]}
                    noPadding
                >
                    <Input placeholder="The actual hyperlink" data-testid="edit-link-form-link" />
                </RVFormItem>
                <Divider />
                <Row justifyContent="flex-end" gap={4}>
                    {!isNew && (
                        <Button htmlType="reset" data-testid="remove-link" onClick={onRemove}>
                            Remove
                        </Button>
                    )}
                    <Button disabled={!formValid} htmlType="submit" type="primary" data-testid="confirm-edit-link">
                        Confirm
                    </Button>
                </Row>
            </Form>
        </div>
    );
}
